/**
 * ATD across all of the provider's buildings (provider Renata Silva, 2026-10-21).
 *
 * Mirrors backend/src/routes/emergeDaySheetAtd.js (GET /emerge/day-sheet/atd) and
 * services/emerge/atdAllFacilities.js. Residents are named only where the provider is care-related; elsewhere the
 * server sends a count and no row.
 *
 * The provider's own location never leaves the browser: distances are computed here, from the building
 * coordinates the server returns and a position the browser gives on a click. Nothing here sends, stores or logs it.
 */
import { api } from "../../../services/api";
import type { BuildingClosure } from "../providerApi";

export type AtdLens = "approved" | "pending" | "dispensed" | "updated";
export const ATD_LENSES: AtdLens[] = ["approved", "pending", "dispensed", "updated"];
export const isAtdLens = (v: string | null | undefined): v is AtdLens => ATD_LENSES.includes(String(v) as AtdLens);

export interface AtdLensInfo {
  key: AtdLens;
  label: string;
  what: string;
  count: number;
  withheld: number;
}

export interface AtdResident {
  patient_id: string;
  first_name: string | null;
  last_name: string | null;
  room_number: string | null;
  /** The ATD word as recorded: Yes, Dispensed, REFUSED, REVERSAL, No, NO-DECEASED, NO-MOVED. */
  atd_status: string | null;
  atd_date: string | null;
  last_change: { to_status: string; effective_date: string | null; at: string | null; in_emerge: boolean } | null;
  dispense_plan: { state: string; visit_on: string | null; pit_stop_on: string | null; attention: string | null } | null;
  lenses: AtdLens[];
}

export interface AtdBuilding {
  id: string;
  name: string | null;
  city: string | null;
  state: string | null;
  address_line: string | null;
  maps: { query: string; from_address: boolean; apple: string; google: string } | null;
  maps_error?: boolean;
  latitude: number | null;
  longitude: number | null;
  closure: BuildingClosure | null;
  counts: Record<AtdLens, number>;
  withheld: Record<AtdLens, number>;
  residents: AtdResident[];
}

export interface AtdAllFacilities {
  lens: AtdLens;
  days: number;
  since: string;
  today: string;
  lenses: AtdLensInfo[];
  buildings: AtdBuilding[];
  scope_buildings: number;
  buildings_without: number;
  truncated: boolean;
  enterprise: boolean;
}

interface ApiResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  status?: number;
}

export const atdAllApi = {
  get: async (lens: AtdLens, days?: number): Promise<ApiResult<AtdAllFacilities>> => {
    const qp = new URLSearchParams({ lens });
    if (days) qp.set("days", String(days));
    return (await api.get(`/emerge/day-sheet/atd?${qp.toString()}`)) as ApiResult<AtdAllFacilities>;
  },
};

/** A position, held in the page only. */
export interface Position {
  latitude: number;
  longitude: number;
}

const EARTH_MILES = 3958.8;
const rad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in miles (haversine). Straight-line, not driving distance. */
export function haversineMiles(a: Position, b: Position): number {
  const dLat = rad(b.latitude - a.latitude);
  const dLng = rad(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_MILES * Math.asin(Math.min(1, Math.sqrt(h)));
}

const hasCoordinates = (b: Pick<AtdBuilding, "latitude" | "longitude">) =>
  typeof b.latitude === "number" && typeof b.longitude === "number" && Number.isFinite(b.latitude) && Number.isFinite(b.longitude);

/** The distance to a building, or null when its location is not recorded. */
export function distanceTo(from: Position, b: Pick<AtdBuilding, "latitude" | "longitude">): number | null {
  return hasCoordinates(b) ? haversineMiles(from, { latitude: b.latitude as number, longitude: b.longitude as number }) : null;
}

/** Nearest first; buildings without a recorded location last, by name. Stable and pure. */
export function sortByDistance<T extends Pick<AtdBuilding, "latitude" | "longitude" | "name" | "id">>(buildings: T[], from: Position): Array<T & { distance: number | null }> {
  return buildings
    .map((b) => ({ ...b, distance: distanceTo(from, b) }))
    .sort((x, y) => {
      if (x.distance === null && y.distance === null) return String(x.name || "").localeCompare(String(y.name || "")) || x.id.localeCompare(y.id);
      if (x.distance === null) return 1;
      if (y.distance === null) return -1;
      return x.distance - y.distance || String(x.name || "").localeCompare(String(y.name || ""));
    });
}

/** "0.4 mi", "12 mi", "120 mi". */
export function milesWords(miles: number): string {
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}
